import axios from './axios';

// api endpoints
const endpoints = {
  getLicenses: '/api/license/requests',
  createLicenseJWT: '/api/license/request/v3/jwt',
  createLicenseGST: '/api/license/request/v2/gst',
  createLicensePlatform: '/api/license/request/v2/platform',
  approveLicense: '/api/license/approve',
  rejectLicense: '/api/license/reject',
  uploadSeed: '/api/license/request/v3/seed',
  contract: '/api/license/contract'
};

/**
 * Get the license requests list (paginated, filter, sort)
 *
 * @param accessToken {string}    user access token
 * @param page {number}           page number (default 1)
 * @param pageSize {number}       page size (default 20)
 * @param sort {string}           sort string, e.g. 'created_at:desc'
 * @param filter {object}         filter object, e.g. { 'use:eq': 'Customer Production', 'type:eq': 'jwt' }
 * @return {Promise<AxiosResponse<T>>}
 */

const getLicenses = async (accessToken, page = 1, pageSize = 20, sort, filter) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`
  };

  const params = {
    page,
    page_size: pageSize
  };

  if (sort) {
    params.sort = sort;
  }
  if (filter) {
    Object.keys(filter).forEach(key => {
      params[`filter[${key}]`] = filter[key];
    });
  }

  try {
    result = await axios.get(endpoints.getLicenses, { headers, params });
  } catch (error) {
    console.log(error);
    result = error;
  }

  return result;
};

/**
 * Create a JWT license request
 *
 * @param accessToken {string}    user access token
 * @param data {object}   license creation form data
 * @return {Promise<AxiosResponse<T>>}
 */

const createLicenseJWT = async (accessToken, data) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`,
    'Content-Type': 'application/json'
  };

  try {
    result = await axios.post(endpoints.createLicenseJWT, data, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Create a GraphStudio license request
 *
 * @param accessToken {string}    user access token
 * @param data {object}   license creation form data
 * @return {Promise<AxiosResponse<T>>}
 */

const createLicenseGST = async (accessToken, data) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`,
    'Content-Type': 'application/json'
  };

  try {
    result = await axios.post(endpoints.createLicenseGST, data, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Create a platform license request
 *
 * @param accessToken {string}    user access token
 * @param data {object}   license creation form data
 * @return {Promise<AxiosResponse<T>>}
 */

const createLicensePlatform = async (accessToken, data) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`,
    'Content-Type': 'application/json'
  };

  try {
    result = await axios.post(endpoints.createLicensePlatform, data, {
      headers
    });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Approve the license request
 *
 * @param accessToken {string}    user access token
 * @param data {object}    license request id and approval notes(optional)
 *                         example: { id, notes }
 *
 * @return {Promise<AxiosResponse<T>>}
 */

const approveLicenseRequest = async (accessToken, data) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`
  };

  try {
    result = await axios.post(endpoints.approveLicense, data, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Reject the license request
 *
 * @param accessToken {string}    user access token
 * @param data {object}    license request id and rejection notes(optional)
 *                         example: { id, notes }
 *
 * @return {Promise<AxiosResponse<T>>}
 */

const rejectLicenseRequest = async (accessToken, data) => {
  let result = undefined;

  const headers = {
    Authorization: `google-token ${accessToken}`
  };

  try {
    result = await axios.post(endpoints.rejectLicense, data, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Upload seed file to get the parsed signature
 *
 * @param token {string}    user token in profile
 * @param seedFile {file}   valid seed file
 * @return {Promise<AxiosResponse<T>>}
 */

const uploadSeed = async (token, seedFile) => {
  let result = undefined;

  const headers = {
    Authorization: `token ${token}`
  };

  try {
    result = await axios.post(endpoints.uploadSeed, seedFile, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Upload contract file to get the UUID
 *
 * @param token {string}    user token in profile
 * @param contractFile {file}   valid contract pdf file
 * @return {Promise<AxiosResponse<T>>}
 */

const uploadContract = async (token, contractFile) => {
  let result = undefined;

  const headers = {
    Authorization: `token ${token}`
  };

  try {
    result = await axios.post(endpoints.contract, contractFile, { headers });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

/**
 * Get contract file by UUID
 *
 * @param token {string}    user token in profile
 * @param contactID {string}   uuid of uploaded PDF
 * @return {Promise<AxiosResponse<T>>}
 */
const getContractFile = async (token, contactID) => {
  let result = undefined;

  const headers = {
    Authorization: `token ${token}`
  };

  try {
    result = await axios.get(`${endpoints.contract}/${contactID}`, {
      headers,
      // Ref: https://github.com/axios/axios/issues/1392
      responseType: 'arraybuffer' // For downloading pdf
    });
  } catch (error) {
    console.log(error);

    result = error;
  }

  return result;
};

export default {
  getLicenses,
  createLicenseJWT,
  createLicenseGST,
  createLicensePlatform,
  approveLicenseRequest,
  rejectLicenseRequest,
  uploadSeed,
  uploadContract,
  getContractFile
};
